import React, { useMemo } from "react";
import Select from "react-select";
import DropDownStyles, { selectMenuPortalProps } from "./dropdownStyles";
import {
  effortTypeForSamplingMethod,
  requiresEffortUnits,
  isAnglingSamplingMethod,
  durationHoursFromRange,
  formatHMS,
  countFishForEffortUnit,
  buildAnglerFishSummary,
} from "../../utils/samplingCpueUtils";
import ElapsedTimeInput from "./ElapsedTimeInput";

const fieldStyle = {
  border: "1px solid #dee2e6",
  borderRadius: "8px",
  height: "45px",
  fontSize: "0.9rem",
};

const readOnlyCountStyle = {
  backgroundColor: "#fff",
  fontWeight: 600,
  color: "#495057",
};

function buildDefaultUnits(method, count) {
  const effortType = effortTypeForSamplingMethod(method);
  if (!effortType || !count) return [];
  const n = parseInt(count, 10);
  if (!Number.isFinite(n) || n < 1) return [];

  const prefix =
    effortType === "electrofishing_area"
      ? "Area"
      : effortType === "seine_haul"
        ? "Haul"
        : "Net";

  return Array.from({ length: n }, (_, i) => ({
    EffortType: effortType,
    Label: `${prefix} ${i + 1}`,
    SortOrder: i,
    TimeStarted: "",
    TimeStopped: "",
    NetNights: effortType === "seine_haul" ? null : 1,
  }));
}

const InlineFishCount = ({ value }) => (
  <div style={{ display: "none" }} aria-hidden="true">
    <label className="small text-muted">Fish sampled</label>
    <div
      className="form-control form-control-sm"
      style={readOnlyCountStyle}
      aria-readonly="true"
    >
      {value}
    </div>
  </div>
);

const SamplingEffortFields = ({
  samplingMethod,
  effortCount,
  onEffortCountChange,
  effortUnits,
  onEffortUnitsChange,
  anglerCount,
  fisherStaffOptions = [],
  selectedSessionAnglers = [],
  onSessionAnglersChange,
  sessionAnglersError,
  fishSamples = [],
  pondAnglers = [],
}) => {
  const effortType = effortTypeForSamplingMethod(samplingMethod);
  const showEffort = requiresEffortUnits(samplingMethod);
  const showAnglers = isAnglingSamplingMethod(samplingMethod);

  const totalElectroHours = useMemo(() => {
    if (effortType !== "electrofishing_area") return 0;
    return (effortUnits || []).reduce(
      (sum, u) => sum + durationHoursFromRange(u.TimeStarted, u.TimeStopped),
      0
    );
  }, [effortUnits, effortType]);

  const anglerSummary = useMemo(
    () => buildAnglerFishSummary(fishSamples, pondAnglers),
    [fishSamples, pondAnglers]
  );

  const effortTotals = useMemo(() => {
    const unassigned = (fishSamples || []).filter((fish) => {
      const unitId = fish.EffortUnitID ?? fish.effortUnitID;
      return unitId == null || unitId === "";
    }).length;
    return {
      unassigned,
      total: (fishSamples || []).length,
    };
  }, [fishSamples]);

  const handleCountChange = (e) => {
    const count = e.target.value;
    onEffortCountChange(count);
    onEffortUnitsChange(buildDefaultUnits(samplingMethod, count));
  };

  const updateUnit = (index, field, value) => {
    const next = [...(effortUnits || [])];
    next[index] = { ...next[index], [field]: value };
    onEffortUnitsChange(next);
  };

  if (!showEffort && !showAnglers) return null;

  const unitLabel =
    effortType === "seine_haul"
      ? "haul"
      : effortType === "electrofishing_area"
        ? "area"
        : "net";

  return (
    <div className="col-12 mt-2">
      {showAnglers && (
        <div className="mb-3">
          <div className="row g-3 align-items-start">
            <div className="col-12 col-md-8 col-lg-9">
              <label className="small text-muted mb-1 d-block">
                Anglers fishing this session
              </label>
              <Select
                isMulti
                options={fisherStaffOptions}
                value={selectedSessionAnglers}
                onChange={onSessionAnglersChange}
                styles={DropDownStyles}
                {...selectMenuPortalProps}
                placeholder="Select staff who fished this session…"
                className={sessionAnglersError ? "is-invalid" : ""}
              />
              {sessionAnglersError && (
                <div className="invalid-feedback d-block">{sessionAnglersError}</div>
              )}
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <div className="form-floating">
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  id="numberOfAnglers"
                  value={anglerCount ?? ""}
                  readOnly
                  style={{ ...fieldStyle, cursor: "not-allowed" }}
                  aria-readonly="true"
                />
                <label htmlFor="numberOfAnglers" className="text-muted small">
                  Number of anglers
                </label>
              </div>
              <p className="small text-muted mb-0 mt-2">
                Auto-calculated from anglers selected for this session.
              </p>
            </div>
          </div>

          {anglerSummary.rows.length > 0 && (
            <div className="mt-3">
              <div className="small fw-semibold text-muted mb-2">
                Fish by angler (auto-calculated)
              </div>
              {anglerSummary.rows.map((row) => (
                <div
                  key={row.staffId ?? row.label}
                  className="d-flex justify-content-between align-items-center py-2 px-2 mb-1 rounded bg-light"
                >
                  <span className="small">{row.label}</span>
                  <InlineFishCount value={row.fishCount} />
                </div>
              ))}
              {anglerSummary.unassigned > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 px-2 mb-1 rounded bg-light">
                  <span className="small text-muted fst-italic">Unassigned</span>
                  <InlineFishCount value={anglerSummary.unassigned} />
                </div>
              )}
              {anglerSummary.total > 0 && (
                <p className="small text-muted mb-0 mt-2" style={{ display: "none" }} aria-hidden="true">
                  Total fish sampled: <strong>{anglerSummary.total}</strong>
                </p>
              )}
            </div>
          )}

          {anglerSummary.rows.length === 0 && anglerSummary.total === 0 && (
            <p className="small text-muted mb-0 mt-2">
              No fish samples yet — enter fish on the Fish Samples tab and assign
              each to an angler.
            </p>
          )}
        </div>
      )}

      {showEffort && (
        <>
          <div className="form-floating mb-3">
            <input
              type="number"
              min="1"
              className="form-control"
              id="effortUnitCount"
              value={effortCount}
              onChange={handleCountChange}
              style={fieldStyle}
            />
            <label htmlFor="effortUnitCount" className="text-muted small">
              {effortType === "electrofishing_area"
                ? "Number of electrofished areas"
                : effortType === "seine_haul"
                  ? "Number of seine hauls"
                  : "Number of nets"}
            </label>
          </div>

          {(effortUnits || []).map((unit, index) => {
            const unitFishCount =
              unit.id != null ? countFishForEffortUnit(fishSamples, unit.id) : 0;

            return (
              <div key={index} className="card border-0 bg-light mb-2 p-3">
                <div className="fw-semibold small mb-2">{unit.Label}</div>

                {effortType === "electrofishing_area" ? (
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <label className="small text-muted">Start (HH:MM:SS)</label>
                      <ElapsedTimeInput
                        className="form-control form-control-sm"
                        placeholder="00:00:00"
                        value={unit.TimeStarted}
                        onChange={(e) =>
                          updateUnit(index, "TimeStarted", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="small text-muted">Stop (HH:MM:SS)</label>
                      <ElapsedTimeInput
                        className="form-control form-control-sm"
                        placeholder="00:14:00"
                        value={unit.TimeStopped}
                        onChange={(e) =>
                          updateUnit(index, "TimeStopped", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-3" style={{ display: "none" }} aria-hidden="true">
                      <label className="small text-muted">Duration</label>
                      <div
                        className="form-control form-control-sm bg-white text-muted"
                        aria-readonly="true"
                      >
                        {formatHMS(
                          durationHoursFromRange(
                            unit.TimeStarted,
                            unit.TimeStopped
                          )
                        )}
                      </div>
                    </div>
                    <div className="col-md-3" style={{ display: "none" }} aria-hidden="true">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                ) : effortType === "seine_haul" ? (
                  <div className="row g-2 align-items-end">
                    <div className="col-md-8">
                      <p className="small text-muted mb-0">1 haul = 1 effort unit</p>
                    </div>
                    <div className="col-md-4">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                ) : (
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <label className="small text-muted">Number of nights</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={unit.NetNights ?? 1}
                        onChange={(e) =>
                          updateUnit(index, "NetNights", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-4">
                      <InlineFishCount value={unitFishCount} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {effortType === "electrofishing_area" && totalElectroHours > 0 && (
            <p className="small text-muted">
              Total sampling time:{" "}
              <strong>{totalElectroHours.toFixed(3)} hrs</strong>
            </p>
          )}

          {effortTotals.unassigned > 0 && (
            <p className="small text-muted mb-1">
              Unassigned fish: <strong>{effortTotals.unassigned}</strong>
            </p>
          )}
          {effortTotals.total > 0 && (
            <p className="small text-muted mb-1" style={{ display: "none" }} aria-hidden="true">
              Total fish sampled: <strong>{effortTotals.total}</strong>
            </p>
          )}

          <p className="small text-muted mb-0 mt-2">
            Enter each sampled fish on the <strong>Fish Samples</strong> tab and
            assign it to the correct {unitLabel} for CPUE. Fish counts update
            automatically.
          </p>
        </>
      )}
    </div>
  );
};

export { buildDefaultUnits };
export default SamplingEffortFields;
